<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { event } from 'vue-gtag'; // Fixed import
import Logo from '@/assets/icon.png';
import Button from 'primevue/button';
import Sidebar from 'primevue/sidebar';
import { useBreakpoints } from '@vueuse/core';


const breakpoints = useBreakpoints({
    mobile: 640,
    tablet: 768,
    desktop: 1024,
});

const isMobile = breakpoints.smaller('desktop');
const isDesktop = breakpoints.greaterOrEqual('desktop');
const route = useRoute();


const isScrolled = ref(false);
const visible = ref(false);

// Check if current route is home page
const isHomePage = computed(() => route.path === '/');

// Only apply scroll effect on desktop home page
const shouldHideOnTop = computed(() => isHomePage.value && !isScrolled.value && isDesktop.value);

const handleScroll = () => {
    isScrolled.value = window.scrollY > 50;
};

// GA Event Tracking Functions
const trackEnrollClick = (location) => {
    event('click', { // Use event function instead of gtag
        event_category: 'Header',
        event_label: 'Enrol Now',
        event_location: location, // 'desktop' or 'mobile'
        custom_parameter_1: 'header_cta'
    });
};

const trackCorporateSiteClick = (location) => {
    event('click', { // Use event function instead of gtag
        event_category: 'Header',
        event_label: 'Corporate Site',
        event_location: location, // 'desktop' or 'mobile'
        custom_parameter_1: 'header_navigation'
    });
};

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
    <nav :class="[
        'fixed top-0 left-0 right-0 w-full transition-all duration-300 ease-in-out z-50',
        {
            // Only hide on desktop home page when not scrolled
            'opacity-0 pointer-events-none -translate-y-full': shouldHideOnTop,
            // Show with backdrop blur effect when scrolled on desktop home page, always show on mobile/tablet, or always show on other pages
            'opacity-100 pointer-events-auto translate-y-0 bg-white/70 backdrop-blur-md shadow-lg border-b border-white/20': isScrolled || !isHomePage || !isDesktop,
            // For mobile/tablet, always show with solid background
            'bg-white shadow-lg border-b border-gray-200': !isDesktop
        }
    ]">
        <div class="flex flex-wrap justify-between items-center mx-auto max-w-screen-xl p-4 md:p-5">
            <!-- Logo Section -->
            <router-link to="/" class="flex items-center justify-center">
                <img :src="Logo" class="h-6 min-[322px]:hidden" alt="BPK PENABUR JAKARTA" />
                
            
                <div class="hidden min-[322px]:flex justify-between">
                    <img :src="Logo" class="h-8 md:h-12 me-2" alt="BPK PENABUR JAKARTA" />
                    <div class="flex flex-col justify-center">
                        <p class="text-xs md:text-sm leading-none p-0 tracking-[0.17em]">Badan Pendidikan Kristen</p>
                        <p class="uppercase text-lg md:text-2xl font-semibold p-0">Penabur <span
                                class="text-[#aeaeaf] font-normal">Jakarta</span></p>
                    </div>
                </div>
            </router-link>
            <ul class="hidden lg:flex space-x-10">
                <li>
                    <router-link to="/"
                        class="text-base capitalize text-gray-700 hover:text-[#314F84] relative group transition-all duration-300 ease-in-out">
                        Home
                    </router-link>
                </li>
                <li>
                    <a href="https://bpkpenabur.or.id/" target="_blank" rel="noopener noreferrer"
                        @click="trackCorporateSiteClick('desktop')"
                        class="text-base capitalize text-gray-700 hover:text-[#314F84] relative group transition-all duration-300 ease-in-out">
                        Corporate Site
                    </a>
                </li>
                
                <li>
                    <router-link to="/schools"
                        class="text-base capitalize text-gray-700 hover:text-[#314F84] relative group transition-all duration-300 ease-in-out">
                        Schools
                    </router-link>
                </li>

                <li>
                    <router-link to="/contact"
                        class="text-base capitalize text-gray-700 hover:text-[#314F84] relative group transition-all duration-300 ease-in-out">
                        Contact Us
                    </router-link>
                </li>
                <li>
                    <router-link to="/faq"
                        class="text-base capitalize text-gray-700 hover:text-[#314F84] relative group transition-all duration-300 ease-in-out">
                        FAQ
                    </router-link>
                </li>
            </ul>

            <!-- Desktop CTA Button -->
            <div class="hidden lg:flex items-center space-x-6 rtl:space-x-reverse">
               
                <router-link to="/enroll">
                    <Button label="Enrol Now" @click="trackEnrollClick('desktop')" severity="penabur" rounded />
                </router-link>  
            </div>

            <!-- Mobile Layout: Enroll Button + Hamburger Menu -->
            <div class="flex lg:hidden items-center space-x-3">
                
                <router-link to="/enroll">
                    <Button label="Enrol Now" @click="trackEnrollClick('mobile')" severity="penabur" rounded />
                </router-link>  
                
                <!-- Mobile Hamburger Button -->
                <Button class="text-[#314F84]" text severity="penabur" @click="visible = true">
                    <Icon icon="lucide:menu" class="w-5 h-5" />
                </Button>
            </div>
        </div>
    </nav>

    <!-- PrimeVue Sidebar for Mobile -->
    <Sidebar v-model:visible="visible" header="" position="right">
        <div class="p-3">
            <!-- Mobile Logo Section -->
            <div class="flex items-center mb-6 pb-4 border-b border-gray-200">
                <!-- Mobile only logo (under 375px) -->
                <img :src="Logo" class="h-8 xs:hidden" alt="BPK PENABUR JAKARTA" />
                
                <!-- Full logo with text (375px and above) -->
                <div class="hidden xs:flex items-center">
                    <img :src="Logo" class="h-10 me-2" alt="BPK PENABUR JAKARTA" />
                    <div>
                        <p class="text-xs leading-none p-0 tracking-[0.15em]">Badan Pendidikan Kristen</p>
                        <p class="uppercase text-lg font-semibold p-0">Penabur <span
                                class="text-[#aeaeaf] font-normal">Jakarta</span></p>
                    </div>
                </div>
            </div>

            <!-- Navigation Menu -->
            <div class="space-y-1">
                <h2 class="mb-2 px-4 text-sm font-medium tracking-tight">Menu</h2>

                <!-- Home -->
                <router-link to="/" @click="visible = false">
                    <Button label="Home" class="w-full text-start" text severity="secondary" />
                </router-link>

                <!-- Corporate Site -->
                <a href="https://bpkpenabur.or.id/" target="_blank" rel="noopener noreferrer"
                    @click="trackCorporateSiteClick('mobile'); visible = false">
                    <Button label="Corporate Site" class="w-full text-start" text severity="secondary" />
                </a>         

                <router-link to="/schools" @click="visible = false">
                    <Button label="School" class="w-full text-start" text severity="secondary" />
                </router-link>
                <!-- Contact Us -->
                <router-link to="/contact" @click="visible = false">
                    <Button label="Contact Us" class="w-full text-start" text severity="secondary" />
                </router-link>

                <!-- FAQ -->
                <router-link to="/faq" @click="visible = false">
                    <Button label="FAQ" class="w-full text-start" text severity="secondary" />
                </router-link>
            </div>
        </div>
    </Sidebar>
</template>